import { ScrollingModule } from "@angular/cdk/scrolling";
import { CommonModule } from "@angular/common";

import {
    Component,
    Injectable,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
    ViewContainerRef,
    inject
} from "@angular/core";

import {
    FormsModule,
    NgForm,
    NgModel,
    ReactiveFormsModule
} from "@angular/forms";

import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef
} from "@angular/material/dialog";

import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTabsModule } from "@angular/material/tabs";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import { TilbyGesturesDirective } from "@tilby/tilby-ui-lib/directives/tilby-gestures";
import { TilbyCurrencyPipe } from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import { lastValueFrom } from "rxjs";

import {
    ConfigurationManagerService,
    EntityManagerService,
    ScreenOrientationService
} from "src/app/core";

import {
    ActiveSaleService
} from "src/app/features";

import {
    keyBy,
    MathUtils,
} from "src/app/shared/utils";

import {
    Categories,
    CategoriesVariations,
    Components,
    Departments,
    Items,
    ItemsComponents,
    ItemsVariations,
    ItemsVariationValues,
    SalesItems,
    SalesItemsIngredients,
    SalesItemsVariations,
} from "tilby-models";

type SaleItemManagerInput = {
    rowItem: SalesItems;
};

type SaleItemManagerOutput = {
    rowItem: SalesItems;
    unbundledRowItem?: SalesItems;
};

type IngredientsForm = (
    Components & { $type: 'component' } |
    ItemsComponents & { $type: 'ingredient' }
) & { quantity: number };

type VariationsForm = (ItemsVariations | CategoriesVariations) & {
    variation_value_id?: number
    quantities?: Record<number, number>
    total_quantity?: number
};

@Component({
    selector: 'app-sale-item-manager-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatOptionModule,
        MatRadioModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatTabsModule,
        ReactiveFormsModule,
        ScrollingModule,
        TilbyDialogContentComponent,
        TilbyGesturesDirective,
        TranslateModule,
    ],
    templateUrl: './sale-item-manager-dialog.component.html',
    styleUrls: ['./sale-item-manager-dialog.component.scss']
})
export class SaleItemManagerDialogComponent implements OnInit {
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly data: SaleItemManagerInput = inject(MAT_DIALOG_DATA);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly screenOrientationService = inject(ScreenOrientationService);
    private readonly tilbyCurrencyPipe = inject(TilbyCurrencyPipe);
    private readonly translateService = inject(TranslateService);

    @ViewChild('optionsOne', { read: TemplateRef }) optionsOne!: TemplateRef<any>;
    @ViewChild('optionsTwo', { read: TemplateRef }) optionsTwo!: TemplateRef<any>;
    @ViewChild('rowItemManagerForm') rowItemManagerForm?: NgForm;
    @ViewChild('formContainerOptionsOne', { read: ViewContainerRef }) formContainerOptionsOne!: ViewContainerRef;
    @ViewChild('formContainerOptionsTwo', { read: ViewContainerRef }) formContainerOptionsTwo!: ViewContainerRef;
    @ViewChildren(NgModel) ngModels!: QueryList<NgModel>;

    protected isMobilePortrait = this.screenOrientationService.isMobilePortrait;
    protected screenHeight = screen.height;

    protected components: Components[] = [];
    protected item?: Items;
    protected title = '';
    protected category?: Categories;
    protected ingredients: IngredientsForm[] = [];
    protected variations: VariationsForm[] = [];
    protected departments: Departments[] = [];
    protected applyAll: boolean = true;
    protected hideAsterisk: boolean = false;

    private ingredientsRemovalPrice = this.configurationManagerService.getPreference("orders.ingredients_removal_affects_price");
    private disableRequiredVariations = this.configurationManagerService.getPreference("cashregister.disable_required_variations");
    private ordersIngredientsRemovalDisabled = this.configurationManagerService.getPreference("orders.ingredients_removal_disabled");
    protected saleHasTable = !!this.activeSaleService.currentSale.room_id;
    protected halfPortionEnabled = this.configurationManagerService.getPreference("orders.half_portion_enable");
    protected disableChangePrice = !this.configurationManagerService.isUserPermitted("cashregister.use_price_changes");
    protected showPrices = this.configurationManagerService.isUserPermitted('show_prices_ui');

    protected rowItem = structuredClone(this.data.rowItem);
    private originalRowItem = structuredClone(this.data.rowItem);
    protected newQuantity: number = 1;
    protected requestWeighing = !!this.data.rowItem.request_weighing;
    protected unbundledQuantity?: number;
    protected exits: { name: string; value: string | number | null }[] = [];
    protected filterText: string = '';
    protected areVariationsSatisfied = false;

    private compileItemInfo(item?: Items) {
        if (!item) {
            return;
        }

        //Set title
        this.title = `${this.translateService.instant("DIALOG.SALE_ITEM_MANAGER.TITLE")} ${item.name}`;

        //Initialize variations (if the item does not use combinations)
        if (!this.rowItem.combination_id) {
            this.variations = structuredClone([...(item.variations || []), ...(this.category?.variations || [])]);

            for (const variation of this.variations) {
                variation.variation_values?.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

                if (this.disableRequiredVariations) {
                    variation.required = false;
                }

                const found = variation.variation_values?.find((value) => value.default_value === true);

                if (found) {
                    variation.variation_value_id = found.id;
                }

                if(variation.max_quantity! >= 1) {
                    variation.quantities = {};
                    variation.total_quantity = 0;
                }
            }
        }

        // Get preference migration.items: if true, sort variations by index
        const migrationItems = !!this.configurationManagerService.getPreference('migration.items');
        
        if(migrationItems) {
            // Adapt cashregister: if new products module then sort variations by index
            this.variations.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
        }

        const itemComponentsMap = keyBy(item?.components || [], (c) => c.id);

        //Initialize Ingredients
        const itemIngredients = [...(item.components || [])].sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()));

        for (const ingredient of itemIngredients) {
            Object.assign(ingredient, {
                $type: 'ingredient',
                quantity: 1
            });
        }

        //Initialize components
        const availableComponents = this.components
            .filter(component => !itemComponentsMap[component.id!] && (!component.categories?.length || component.categories.some(category => category.id === item.category_id)))
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
            .map(c => structuredClone(c));

        for (let component of availableComponents) {
            Object.assign(component, {
                $type: 'component',
                quantity: 0
            });
        }

        this.ingredients = [...itemIngredients, ...availableComponents] as IngredientsForm[];
    }

    async ngOnInit() {
        await this.loadInfo();
        this.compileItemInfo(this.item);

        if (this.rowItem.quantity) {
            this.newQuantity = this.rowItem.quantity;

            const ingredientsMap = keyBy(this.ingredients, i => i.id);
            const variationsMap = keyBy(this.variations, v => v.id);

            //Map existing variations
            for (const variation of this.rowItem.variations || []) {
                const found = variationsMap[variation.variation_id];

                if (found) {
                    found.variation_value_id = variation.variation_value_id;
                }

                if(found.quantities) {
                    found.quantities[variation.variation_value_id] = variation.quantity ?? 1;
                }
            }

            for (const variation of this.variations) {
                this.updateVariationTotalQuantity(variation);
            }

            //Map existing ingredient variations
            for (const ingredient of this.rowItem.ingredients || []) {
                const found = ingredientsMap[ingredient.ingredient_id];
                const ingredientQuantity = ingredient.quantity || 0;

                if (found) {
                    switch (ingredient.type) {
                        case 'removed':
                            found.quantity = -1;
                            break;
                        case 'added':
                            found.quantity = (found.$type === 'ingredient') ? (ingredientQuantity + 1) : ingredientQuantity;
                            break;
                    }
                }
            }
        } else {
            this.newQuantity = 1;
        }

        const totalExits = +((this.configurationManagerService.getPreference('orders.exits')) || 5);

        this.exits = [{
            name: this.translateService.instant("ORDERS.ORDER_ITEM_MANAGER.NO_EXIT"),
            value: null
        }];

        for (let exit = 1; exit <= totalExits; exit++) {
            this.exits.push({
                name: `${this.translateService.instant("ORDERS.ORDER_ITEM_MANAGER.EXIT_LABEL")} ${exit}`,
                value: exit
            });
        }

        this.areVariationsSatisfied = this.checkMandatoryVariations();
    }

    ngAfterViewInit() {
        this.formContainerOptionsOne.createEmbeddedView(this.optionsOne);
        this.formContainerOptionsTwo.createEmbeddedView(this.optionsTwo);

        setTimeout(() => {
            this.ngModels.forEach(control => {
                if (control.name && !this.rowItemManagerForm?.controls[control.name]) {
                    this.rowItemManagerForm?.addControl(control);
                }
            });
        });

        if (!this.showPrices) {
            setTimeout(() => {
                if (this.rowItemManagerForm?.controls.price) {
                    this.hideAsterisk = true;
                }
            });
        }
    }

    /**
     * Handles the price change event.
     * If prices are not shown, it temporarily unhides the asterisk indicator for price input.
     */
    onPriceChange(event: any) {
        if (!this.showPrices) {
            const userInput = event.target.value;

            if (userInput) {
                this.hideAsterisk = false;

                setTimeout(() => {
                    this.hideAsterisk = true;
                }, 1000);
            } else {
                this.hideAsterisk = false;
            }
        }
    }

    /**
     * Asynchronously loads essential information for the dialog, including components, departments, item, and category.
     * @returns {Promise<void>}
     */
    async loadInfo() {
        this.components = await this.entityManagerService.components.fetchCollectionOffline();
        this.departments = await this.entityManagerService.departments.fetchCollectionOffline().then(deps => deps.sort((a, b) => a.name.localeCompare(b.name)));
        this.item = (this.data.rowItem.item_id ? await this.entityManagerService.items.fetchOneOffline(this.data.rowItem.item_id) : undefined);
        this.category = (this.data.rowItem.category_id ? await this.entityManagerService.categories.fetchOneOffline(this.data.rowItem.category_id) : undefined);
    }

    /**
     * Parses the selected components and ingredients into `SalesItemsIngredients` format.
     * This includes both newly added components and modifications to existing ingredients (added/removed).
     * @returns {SalesItemsIngredients[]} An array of parsed sales item ingredients.
     */
    parseNewComponents(): SalesItemsIngredients[] {
        return this.ingredients
            .filter((ingredient) => (
                (ingredient.$type === 'component' && ingredient.quantity !== 0) ||
                (ingredient.$type === 'ingredient' && ingredient.quantity !== 1)
            ))
            .map((ingredient) => {
                const quantity = (ingredient.$type === 'ingredient' && ingredient.quantity > 1)
                    ? (ingredient.quantity - 1)
                    : Math.abs(ingredient.quantity);

                return {
                    type: ingredient.quantity === -1 ? 'removed' : 'added',
                    name: ingredient.name,
                    ingredient_id: ingredient.id!,
                    price_difference: ingredient.price_difference,
                    quantity: quantity
                }
            });
    }

    /**
     * Calculates and returns the price difference for a variation value as a formatted string.
     * @param {ItemsVariationValues} variationValue The variation value for which to get the price difference.
     * @returns {string} The formatted price difference string (e.g., "+$5.00", "-$2.50", or empty if no difference).
     */
    getPriceDifferenceValueString(variationValue: ItemsVariationValues) {
        const value = this.getPriceDifferenceValue(variationValue);

        if (value == null) {
            return '';
        }

        return value > 0 ? `+${this.tilbyCurrencyPipe.transform(value)}` : this.tilbyCurrencyPipe.transform(value);
    }

    /**
     * Calculates the actual numerical price difference based on the variation value's price difference type.
     * @param {ItemsVariationValues} variationValue The variation value for which to calculate the price difference.
     * @returns {number | null | undefined} The calculated price difference, or null/undefined if not applicable.
     */
    getPriceDifferenceValue(variationValue: ItemsVariationValues) {
        if (variationValue.price_difference == null) {
            return variationValue.price_difference;
        }

        switch (variationValue.price_difference_type) {
            case 'perc':
                return MathUtils.round(this.rowItem.price * (variationValue.price_difference / 100));
            case 'value':
            default:
                return variationValue.price_difference;
        }
    }

    /**
     * Parses the selected variations into `SalesItemsVariations` format.
     * Handles both standard variations and required/mandatory variations with quantities.
     * @returns {SalesItemsVariations[]} An array of parsed sales item variations.
     */
    parseNewVariations() {
        const newVariations: SalesItemsVariations[] = [];

        if (this.rowItem.combination_id) {
            return newVariations;
        }

        for (const variation of this.variations) {
            if(variation.max_quantity! >= 1) {
                for(const [valueId, quantity] of Object.entries(variation.quantities || {})) {
                    if(!quantity) {
                        continue;
                    }

                    const variationValueId = +valueId;
                    const found = variation.variation_values?.find((element) => element.id === variationValueId);

                    if(!found) {
                        continue;
                    }

                    newVariations.push({
                        name: variation.name,
                        value: found.value!,
                        price_difference: this.getPriceDifferenceValue(found),
                        linked_item_uuid: found.linked_item_uuid,
                        linked_item_sku: found.linked_item_sku,
                        quantity: quantity,
                        variation_id: variation.id!,
                        variation_value_id: variationValueId
                    });
                }
            } else if(variation.variation_value_id) {
                const found = variation.variation_values?.find((element) => element.id === variation.variation_value_id);
    
                if (!found) {
                    continue;
                }

                newVariations.push({
                    name: variation.name,
                    value: found.value!,
                    price_difference: this.getPriceDifferenceValue(found),
                    linked_item_uuid: found.linked_item_uuid,
                    linked_item_sku: found.linked_item_sku,
                    variation_id: variation.id!,
                    variation_value_id: variation.variation_value_id!
                });
            }
        }

        return newVariations;
    };

    /**
     * Calculates and returns the price difference for an ingredient as a formatted string.
     * @param {IngredientsForm} ingredient The ingredient for which to get the price difference.
     * @returns {string} The formatted price difference string.
     */
    getPriceDifference(ingredient: IngredientsForm) {
        return (!ingredient.price_difference || (!this.ingredientsRemovalPrice && ingredient.$type === 'ingredient'))
            ? '' 
            : `${ingredient.price_difference >= 0 ? '+' : ''}${this.tilbyCurrencyPipe.transform(ingredient.quantity > 1 ? ingredient.price_difference * ingredient.quantity : ingredient.price_difference)}`;
    };

    /**
     * Increments the quantity of an ingredient.
     * @param {IngredientsForm} ingredient The ingredient whose quantity needs to be incremented.
     */
    incrementIngredientQuantity(ingredient: IngredientsForm) {
        if (ingredient.quantity <= -1) {
            switch (ingredient.$type) {
                case 'ingredient':
                    ingredient.quantity = 1;
                    break;
                case 'component':
                    ingredient.quantity = 0;
                    break;
            }
        } else {
            ingredient.quantity++;
        }
    };

    /**
     * Decrements the quantity of an ingredient.
     * @param {IngredientsForm} ingredient The ingredient whose quantity needs to be decremented.
     */
    decrementIngredientQuantity(ingredient: IngredientsForm) {
        switch (ingredient.$type) {
            case 'ingredient':
                if (ingredient.quantity <= 1) {
                    ingredient.quantity = -1;
                } else {
                    ingredient.quantity--;
                }
                break;
            case 'component':
                if (this.ordersIngredientsRemovalDisabled) {
                    if (ingredient.quantity > 0) {
                        ingredient.quantity--;
                    }
                } else {
                    if (ingredient.quantity <= 0) {
                        ingredient.quantity = -1;
                    } else {
                        ingredient.quantity--;
                    }
                }
                break;
        }
    };

    /**
     * Processes the current state of the dialog and closes it, returning the modified sale items.
     * If an unbundled quantity is specified, it handles splitting the item into two sale items.
     */
    answer() {
        const selectedDepartment = this.departments.find((department) => department.id === this.rowItem.department_id);

        if (!selectedDepartment) {
            return;
        }

        const unbundledQuantity = this.unbundledQuantity || 0;
        const newQuantity = this.newQuantity;

        const rowItem: SalesItems = {
            ...this.rowItem,
            department: selectedDepartment,
            department_id: selectedDepartment.id!,
            department_name: selectedDepartment.name!,
            half_portion: !!this.rowItem.half_portion,
            ingredients: this.parseNewComponents(),
            quantity: newQuantity,
            vat_perc: selectedDepartment.vat?.value!,
            variations: this.parseNewVariations()
        };

        if (this.rowItem.request_weighing) {
            rowItem.request_weighing = this.requestWeighing;
        }

        let originalRowItem: SalesItems | undefined;

        if (unbundledQuantity && unbundledQuantity < newQuantity) {
            originalRowItem = {
                ...structuredClone(this.originalRowItem),
                quantity: MathUtils.round(newQuantity - unbundledQuantity),
            };

            rowItem.quantity = unbundledQuantity;

            // both rows now have a manual quantity: there is nothing left to weigh
            if (this.rowItem.request_weighing) {
                originalRowItem.request_weighing = false;
                rowItem.request_weighing = false;
            }
        }

        const output: SaleItemManagerOutput = {
            rowItem: originalRowItem || rowItem,
            unbundledRowItem: originalRowItem ? rowItem : undefined
        };

        this.matDialogRef.close(output);
    };

    /**
     * Filters the ingredients based on the `filterText`.
     * @returns {IngredientsForm[]} An array of filtered ingredients.
     */
    filteredIngredients() {
        const filterText = this.filterText.toLowerCase();

        return this.ingredients.filter((ingredient) => ingredient.name!.toLowerCase().includes(filterText));
    }

    /**
     * Marks the item for deletion by setting its quantity to 0 and then calls `answer()`.
     * This effectively removes the item from the sale when the dialog closes.
     * @returns {Promise<void>}
     */
    async deleteItem() {
        this.applyAll = true;
        this.newQuantity = 0;
        this.answer();
    }

    private changeVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues, quantityDelta: number) {
        // Ensure variation and variation value IDs exist
        if (!variation.id || !variationValue.id) {
            return;
        }

        const variationQuantities = variation.quantities || {};
        const currentQuantity = variationQuantities[variationValue.id] || 0;

        // Update the quantity for the variation value
        variationQuantities[variationValue.id] = currentQuantity + quantityDelta;

        variation.quantities = variationQuantities;
        this.updateVariationTotalQuantity(variation);

        this.areVariationsSatisfied = this.checkMandatoryVariations();
    }

    /**
     * Updates the total quantity of a variation by summing up the quantities of its variation values.
     * @param {VariationsForm} variation The variation whose total quantity needs to be updated.
     */
    updateVariationTotalQuantity(variation: VariationsForm) {
        if(!variation.quantities) {
            return;
        }

        variation.total_quantity = Object.values(variation.quantities || {}).reduce((a, b) => a + b, 0);
    }

    /**
     * Increases the quantity of a specific variation value.
     * @param {VariationsForm} variation The variation object.
     * @param {ItemsVariationValues} variationValue The specific variation value to increase.
     */
    increaseVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues) {
        this.changeVariationQuantity(variation, variationValue, + 1);
    }

    /**
     * Decreases the quantity of a specific variation value.
     * @param {VariationsForm} variation The variation object.
     * @param {ItemsVariationValues} variationValue The specific variation value to decrease.
     */
    decreaseVariationQuantity(variation: VariationsForm, variationValue: ItemsVariationValues) {
        this.changeVariationQuantity(variation, variationValue, -1);
    }

    onVariationChange() {
        this.areVariationsSatisfied = this.checkMandatoryVariations();
    }

    /**
     * Checks if all mandatory variations are satisfied based on selected quantities.
     * A variation is considered mandatory if it's marked as `required` and has a `max_quantity` of 1 or more.
     * @returns {boolean} True if all mandatory variations are satisfied, false otherwise.
     */
    checkMandatoryVariations() {
        for(const variation of this.variations) {
            if(variation.max_quantity && variation.max_quantity! >= 1) {
                if(variation.min_quantity && variation.total_quantity! < variation.min_quantity) {
                    return false;
                }

                if(variation.total_quantity! > variation.max_quantity) {
                    return false;
                }
            } else if(variation.required && !variation.variation_value_id) {
                return false;
            }
        }

        return true;
    }
}

@Injectable({
    providedIn: 'root'
})
export class SaleItemManagerDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    /**
     * Opens the Sale Item Manager dialog.
     * @param {SaleItemManagerInput} data The input data for the dialog, containing the sale item to manage.
     * @returns {Promise<SaleItemManagerOutput>} A promise that resolves with the output data when the dialog is closed.
     */
    public openDialog(data: SaleItemManagerInput): Promise<SaleItemManagerOutput> {
        const config: NonNullableConfigData<any> = { ...this.switchMobileDesktopDimensions({ height: '90vh', width: '90%' }), disableClose: true, data };

        return lastValueFrom(this.dialogRef.open(SaleItemManagerDialogComponent, config).afterClosed());
    }
}
